import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import type { AvailabilityStatus } from "@/features/shops/api/useCheckAvailability";
import { formatReservationDateTime } from "@/lib/formatReservationDateTime";
import { colors } from "@/theme/colors";

type Props = {
  loading: boolean;
  preferredDate: string;
  preferredTime: string;
  availability: AvailabilityStatus;
};

/**
 * AvailabilityChip は空席確認状態のピル型ラベル (JPTP_CANSEI-1443)。
 * 条件不足 (unconfigured。日時・人数が抽出できない) と確認エラー (error) は、ゲスト向け画面の
 * ためエラーを露出させずチップ自体を表示しない。
 */
function AvailabilityChip({ status }: { status: AvailabilityStatus }) {
  const { t } = useTranslation();

  if (status === "unconfigured" || status === "error") return null;

  if (status === "loading") {
    return (
      <Chip
        label={t("shopDetail.checkingAvailability")}
        size="small"
        sx={{
          bgcolor: colors.surfaceMuted,
          color: colors.textMuted,
          fontWeight: 700,
          borderRadius: "999px",
        }}
      />
    );
  }

  const available = status === "available";
  const Icon = available ? EventAvailableIcon : EventBusyIcon;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: 16, color: "#fff !important" }} />}
      label={available ? t("shopDetail.available") : t("shopDetail.unavailable")}
      size="small"
      sx={{
        bgcolor: available ? colors.availability : colors.availabilityNone,
        color: "#fff",
        fontWeight: 700,
        borderRadius: "999px",
      }}
    />
  );
}

/**
 * 予約希望日時 + 空席確認チップの表示ブロック。
 * 予約希望日時は GetReservationContext (案件のメール解析結果) から取得する。
 * AI が抽出できなかった場合は空文字で返るため "-" を表示する。
 */
export function ReservationSection({ loading, preferredDate, preferredTime, availability }: Props) {
  const { t, i18n } = useTranslation();
  const formatted = formatReservationDateTime(preferredDate, preferredTime, i18n.language);
  const displayDateTime = loading ? t("shopDetail.loading") : formatted || "-";

  return (
    <Box>
      <Typography sx={{ color: colors.textMuted, fontSize: 14, fontWeight: 600, mb: 0.5 }}>
        {t("shopDetail.reservationDateTime")}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Typography sx={{ color: colors.text, fontSize: 20, fontWeight: 700 }}>
          {displayDateTime}
        </Typography>
        <AvailabilityChip status={availability} />
      </Box>
    </Box>
  );
}
